import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import AllocBarCanvas from "components/AllocBarCanvas";
import { useAppContext } from "components/Context/AppContext";
import "styles/dashboard.css";
import "./risk.css";

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// ── Colour helpers ─────────────────────────────────────────────────────────────
function hexToRgb(h) {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function lerpColour(a, b, t) {
  const ca = hexToRgb(a), cb = hexToRgb(b);
  return `rgb(${Math.round(ca[0] + (cb[0] - ca[0]) * t)},${Math.round(ca[1] + (cb[1] - ca[1]) * t)},${Math.round(ca[2] + (cb[2] - ca[2]) * t)})`;
}

// ── SVG Gauge ──────────────────────────────────────────────────────────────────
function SvgGauge({ min = 0, max, value, target, targetLabel, colourFn, reading, readingColor }) {
  const cx = 140, cy = 148, r = 105, SEGS = 80;
  const toDeg = (v) => 180 - ((v - min) / (max - min)) * 180;
  const toXY  = (deg, radius) => ({
    x: cx + radius * Math.cos((deg * Math.PI) / 180),
    y: cy - radius * Math.sin((deg * Math.PI) / 180),
  });

  const segments = [];
  for (let i = 0; i < SEGS; i++) {
    const v0 = min + (i / SEGS) * (max - min);
    const v1 = min + ((i + 1) / SEGS) * (max - min);
    const p0 = toXY(toDeg(v0), r), p1 = toXY(toDeg(v1), r);
    segments.push(
      <path key={i}
        d={`M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${r} ${r} 0 0 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`}
        fill="none" stroke={colourFn((v0 + v1) / 2)} strokeWidth="14" strokeLinecap="butt"
      />
    );
  }

  const clampedValue = Math.max(min, Math.min(max, value));
  const tip    = toXY(toDeg(clampedValue), r - 5);
  const ta     = toDeg(target);
  const tOuter = toXY(ta, r + 11), tInner = toXY(ta, r - 11);
  const lp     = toXY(ta, r + 24);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg viewBox="0 0 280 148" width="100%"
        style={{ display: "block", overflow: "visible", maxWidth: 175 }}>
        <defs>
          <filter id="rsk-nd">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="rgba(0,0,0,0.5)" />
          </filter>
        </defs>
        {segments}
        <line
          x1={cx} y1={cy} x2={tip.x.toFixed(2)} y2={tip.y.toFixed(2)}
          stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round"
          filter="url(#rsk-nd)"
        />
        <circle cx={cx} cy={cy} r="5.5" fill="var(--dash-bg)" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="2" fill="rgba(255,255,255,0.7)" />
        <line
          x1={tOuter.x.toFixed(2)} y1={tOuter.y.toFixed(2)}
          x2={tInner.x.toFixed(2)} y2={tInner.y.toFixed(2)}
          stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round"
        />
        <text
          x={lp.x.toFixed(1)} y={lp.y.toFixed(1)}
          fontSize="12" fill="var(--dash-muted)"
          textAnchor="middle" fontFamily="'DM Mono', monospace"
        >
          {targetLabel ?? target}
        </text>
      </svg>
      <div style={{
        fontFamily: "var(--dash-font-d)", fontSize: "1.3rem",
        color: readingColor, textAlign: "center", lineHeight: 1, marginTop: -2,
      }}>
        {reading}
      </div>
    </div>
  );
}

// ── Concentration row ──────────────────────────────────────────────────────────
function concColour(ratio) {
  return ratio <= 0.95 ? "#22d47e" : ratio <= 1.05 ? "#C9A84C" : "#e05252";
}

function ConcRow({ name, ratio, maxRatio }) {
  const c  = concColour(ratio);
  const vp = ((ratio / maxRatio) * 100).toFixed(2);
  const lp = ((1 / maxRatio) * 100).toFixed(2);
  return (
    <div className="risk-conc-row">
      <div className="risk-conc-name">{name}</div>
      <div style={{ flex: 1, position: "relative", height: 16 }}>
        <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "var(--dash-border)", transform: "translateY(-50%)" }} />
        <div style={{ position: "absolute", top: "50%", left: 0, width: `${vp}%`, height: 2, background: c, opacity: 0.3, transform: "translateY(-50%)" }} />
        <div style={{ position: "absolute", top: "50%", left: `${vp}%`, width: 8, height: 8, borderRadius: "50%", background: c, transform: "translate(-50%,-50%)", boxShadow: `0 0 5px ${c}55` }} />
        <div style={{ position: "absolute", top: 2, bottom: 2, left: `${lp}%`, width: 1.5, background: "var(--dash-border2)", transform: "translateX(-50%)", borderRadius: 1 }} />
      </div>
      <div className="risk-conc-val" style={{ color: c }}>
        {ratio < 1 ? ratio.toFixed(2) : ratio.toFixed(1)}x
      </div>
    </div>
  );
}

// ── Contributor row ────────────────────────────────────────────────────────────
function ContribRow({ name, pct, maxAbs }) {
  const isHedge  = pct < 0;
  const barWidth = `${((Math.abs(pct) / maxAbs) * 100).toFixed(1)}%`;
  const barColor = isHedge ? "var(--dash-gold-text)" : "var(--dash-blue)";
  return (
    <div className="risk-contrib-row">
      <div className="risk-contrib-name">{name}</div>
      <div className="risk-contrib-bar-track">
        <div className="risk-contrib-bar-fill" style={{ width: barWidth, background: barColor, opacity: 0.85 }} />
      </div>
      <div className="risk-contrib-val" style={{ color: isHedge ? "var(--dash-gold-text)" : "var(--dash-text)" }}>
        {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
      </div>
    </div>
  );
}

// ── Alloc chart card (card chrome + expand button + canvas) ───────────────────
function AllocCard({ title, levels, theme, onExpand }) {
  const [currentLevel, setCurrentLevel] = useState("all");
  if (!levels) {
    return (
      <div className="trg-card">
        <div className="trg-card-header"><div className="trg-card-title">{title}</div></div>
        <div className="risk-alloc-body trg-no-data">No data</div>
      </div>
    );
  }
  return (
    <div className="trg-card">
      <div className="trg-card-header">
        <div className="trg-card-title">{title}</div>
        <button className="trg-expand-btn" onClick={() => onExpand(title, currentLevel)}>⛶</button>
      </div>
      <div className="risk-alloc-body">
        <AllocBarCanvas
          levels={levels}
          theme={theme}
          initialLevel="all"
          onLevelChange={setCurrentLevel}
          showVar
        />
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function RiskPage() {
  const { selectedAccount } = useAppContext();
  const [theme, setTheme]   = useState("dark");
  const [fsPanel, setFsPanel] = useState(null);

  const [parameters,    setParameters]    = useState(null);
  const [summary,       setSummary]       = useState(null);
  const [concentrations,setConcentrations]= useState(null);
  const [contributions, setContributions] = useState(null);
  const [assetLevels,   setAssetLevels]   = useState(null);
  const [regionLevels,  setRegionLevels]  = useState(null);
  const [industryLevels,setIndustryLevels]= useState(null);
  const [currencyLevels,setCurrencyLevels]= useState(null);
  const [loading,       setLoading]       = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const aid   = selectedAccount?.account_id;
    if (!token || !aid) return;

    const q = `?token=${token}&account_id=${aid}`;
    setLoading(true);

    Promise.all([
      fetch(`${DASHBOARD_API}/risk/parameters${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/summary${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/concentrations${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/contributions${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/asset${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/region${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/industry${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/currency${q}`).then(r => r.json()),
    ])
      .then(([params, summ, conc, contrib, asset, region, industry, currency]) => {
        setParameters(Array.isArray(params) ? params : null);
        setSummary(summ && summ.measures ? summ : null);
        setConcentrations(Array.isArray(conc) && conc.length ? conc : null);
        setContributions(Array.isArray(contrib) && contrib.length ? contrib : null);
        setAssetLevels(asset && !asset.error ? asset : null);
        setRegionLevels(region && !region.error ? region : null);
        setIndustryLevels(industry && !industry.error ? industry : null);
        setCurrencyLevels(currency && !currency.error ? currency : null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  // ── Gauge colour functions (only used when summary is available) ──
  const gaugeSharpe = summary?.gaugeSharpe;
  const gaugeRisk   = summary?.gaugeRisk;

  const sharpeColour = (v) => {
    if (!gaugeSharpe) return "#C9A84C";
    const { target, band, max } = gaugeSharpe;
    const p = v / max;
    const lo = (target - band) / max, hi = (target + band) / max;
    if (p <= lo) return "#e05252";
    if (p <= hi) return lerpColour("#e05252", "#C9A84C", (p - lo) / (hi - lo));
    return lerpColour("#C9A84C", "#22d47e", Math.min(1, (p - hi) / (1 - hi)));
  };

  const riskMax    = gaugeRisk ? gaugeRisk.limit * 1.15 : 30;
  const riskColour = (v) => {
    if (!gaugeRisk) return "#C9A84C";
    const { limit, band } = gaugeRisk;
    const p = v / riskMax;
    const pY = (limit - band) / riskMax, pR = (limit + band) / riskMax;
    if (p <= pY) return lerpColour("#22d47e", "#C9A84C", Math.min(1, p / pY));
    if (p <= pR) return lerpColour("#C9A84C", "#e05252", (p - pY) / (pR - pY));
    return "#e05252";
  };

  // ── Reading colours ──
  const sharpeDiff      = gaugeSharpe ? gaugeSharpe.value - gaugeSharpe.target : 0;
  const sharpeReadColor = gaugeSharpe
    ? (sharpeDiff >  gaugeSharpe.band ? "#22d47e" : sharpeDiff > -gaugeSharpe.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  const varDiff      = gaugeRisk ? gaugeRisk.value - gaugeRisk.limit : 0;
  const varReadColor = gaugeRisk
    ? (varDiff < -gaugeRisk.band ? "#22d47e" : varDiff < gaugeRisk.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // ── Derived max values ──
  const concMax    = concentrations ? Math.max(...concentrations.map(d => d.ratio)) * 1.1 : 1;
  const contribMax = contributions  ? Math.max(...contributions.map(d => Math.abs(d.pct))) : 1;

  // ── Fullscreen helpers ──
  const openAllocFs = (title, initialLevel, levels) => setFsPanel({
    title,
    content: (
      <AllocBarCanvas levels={levels} theme={theme} initialLevel={initialLevel} showVar />
    ),
  });

  const openConcFs = () => {
    if (!concentrations) return;
    setFsPanel({
      title: "Concentrations vs Limits",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 20, padding: "0.5rem 0" }}>
          {concentrations.map(d => (
            <ConcRow key={d.name} name={d.name} ratio={d.ratio} maxRatio={concMax} />
          ))}
        </div>
      ),
    });
  };

  const openContribFs = () => {
    if (!contributions) return;
    setFsPanel({
      title: "Top Risk Contributors / Hedges",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.5rem 0" }}>
          {contributions.map(d => (
            <ContribRow key={d.name} name={d.name} pct={d.pct} maxAbs={contribMax} />
          ))}
        </div>
      ),
    });
  };

  const closeFs = () => setFsPanel(null);

  const fundName = selectedAccount?.account_name || selectedAccount?.short_name || "Company Name";
  const measures = summary?.measures ?? [];

  return (
    <>
      <Helmet>
        <title>Risk – Tail Risk Global</title>
      </Helmet>

      <div className={`trg-shell${theme === "light" ? " theme-light" : ""}`}>
        <DashboardTopbar />
        <DashboardMenuBar onThemeChange={setTheme} />

        <main className="risk-body">

          {/* ── Page header ── */}
          <div className="risk-page-hdr">
            <div className="risk-page-hdr-left">
              <div className="trg-fund-eyebrow">Risk Analytics</div>
              <div className="trg-fund-divider" />
              <div className="trg-fund-name">{fundName}</div>
            </div>
            <div style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.58rem", color: "var(--dash-muted2)", letterSpacing: "0.06em" }}>
              As of 10 Apr 2026 · USD
            </div>
          </div>

          {loading && <div className="trg-loading">Loading…</div>}

          {/* ── Row 1: Portfolio Settings · Risk Measures · Gauges ── */}
          <div className="risk-row1">

            {/* Portfolio Settings */}
            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Portfolio Settings</div>
              </div>
              <div className="risk-settings-body">
                {parameters && parameters.length > 0
                  ? parameters.map(({ label, value, badge }) => (
                      <div key={label} className="risk-settings-row">
                        <span className="risk-settings-label">{label}</span>
                        {badge
                          ? <span className="risk-meta-badge">{value}</span>
                          : <span className="risk-settings-val">{value}</span>
                        }
                      </div>
                    ))
                  : !loading && <div className="trg-no-data">No data</div>
                }
              </div>
            </div>

            {/* Risk Measures */}
            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Risk Measures</div>
                <div style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.55rem", color: "var(--dash-muted2)" }}>USD</div>
              </div>
              <div className="risk-strip">
                {measures.length > 0
                  ? measures.map(({ label, value, unit, sub }) => (
                      <div key={label} className="risk-strip-item">
                        <div className="risk-strip-label">{label}</div>
                        <div className="risk-strip-value">
                          {value}
                          {unit && (
                            <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.825rem", color: "var(--dash-muted)", marginLeft: 2 }}>
                              {unit}
                            </span>
                          )}
                        </div>
                        {sub && <div className="risk-strip-sub">{sub}</div>}
                      </div>
                    ))
                  : !loading && <div className="trg-no-data">No data</div>
                }
              </div>
            </div>

            {/* Gauges */}
            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Gauges</div>
              </div>
              {gaugeSharpe && gaugeRisk ? (
                <>
                  <div className="risk-gauges-row">
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">
                        <span title="Risk-Adjusted Expected Return" style={{ cursor: "help" }}>RAER</span>
                        &nbsp;vs BMK
                      </div>
                      <SvgGauge
                        min={0}
                        max={gaugeSharpe.max}
                        value={gaugeSharpe.value}
                        target={gaugeSharpe.target}
                        targetLabel={gaugeSharpe.target.toFixed(2)}
                        colourFn={sharpeColour}
                        reading={gaugeSharpe.value.toFixed(2)}
                        readingColor={sharpeReadColor}
                      />
                    </div>
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">Risk vs Limit</div>
                      <SvgGauge
                        min={0}
                        max={riskMax}
                        value={gaugeRisk.value}
                        target={gaugeRisk.limit}
                        targetLabel={`${gaugeRisk.limit.toFixed(0)}M`}
                        colourFn={riskColour}
                        reading={
                          <>{gaugeRisk.value.toFixed(1)}<span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.75rem", color: "var(--dash-muted)", marginLeft: 2 }}>M</span></>
                        }
                        readingColor={varReadColor}
                      />
                    </div>
                  </div>
                  <div className="risk-gauge-note">Both gauges use VaR 95% 1D</div>
                </>
              ) : (
                !loading && <div className="trg-no-data">No data</div>
              )}
            </div>

          </div>{/* /row1 */}

          {/* ── Row 2: Concentrations · Contributors ── */}
          <div className="risk-row2">

            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Concentrations vs Limits</div>
                {concentrations && <button className="trg-expand-btn" onClick={openConcFs}>⛶</button>}
              </div>
              <div className="risk-conc-body">
                {concentrations
                  ? (
                    <div className="risk-conc-rows">
                      {concentrations.map(d => (
                        <ConcRow key={d.name} name={d.name} ratio={d.ratio} maxRatio={concMax} />
                      ))}
                    </div>
                  )
                  : !loading && <div className="trg-no-data">No data</div>
                }
              </div>
            </div>

            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Top Risk Contributors / Hedges</div>
                {contributions && <button className="trg-expand-btn" onClick={openContribFs}>⛶</button>}
              </div>
              <div className="risk-contrib-body">
                {contributions
                  ? contributions.map(d => (
                      <ContribRow key={d.name} name={d.name} pct={d.pct} maxAbs={contribMax} />
                    ))
                  : !loading && <div className="trg-no-data">No data</div>
                }
              </div>
            </div>

          </div>{/* /row2 */}

          {/* ── Allocation 2×2 ── */}
          <div className="risk-alloc-grid">
            <AllocCard
              title="Asset Allocation"
              levels={assetLevels}
              theme={theme}
              onExpand={(title, level) => openAllocFs(title, level, assetLevels)}
            />
            <AllocCard
              title="Region Allocation"
              levels={regionLevels}
              theme={theme}
              onExpand={(title, level) => openAllocFs(title, level, regionLevels)}
            />
            <AllocCard
              title="Industry Allocation"
              levels={industryLevels}
              theme={theme}
              onExpand={(title, level) => openAllocFs(title, level, industryLevels)}
            />
            <AllocCard
              title="Currency Allocation"
              levels={currencyLevels}
              theme={theme}
              onExpand={(title, level) => openAllocFs(title, level, currencyLevels)}
            />
          </div>

        </main>

        {/* ── Fullscreen overlay ── */}
        {fsPanel && (
          <div
            className="trg-fs-overlay active"
            onClick={(e) => { if (e.target === e.currentTarget) closeFs(); }}
          >
            <div className="trg-fs-panel">
              <div className="trg-fs-header">
                <div className="trg-fs-title">{fsPanel.title}</div>
                <button className="trg-fs-close" onClick={closeFs}>✕ close</button>
              </div>
              <div className="trg-fs-body">
                {fsPanel.content}
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
